import { get as _get } from 'lodash';

// Reason codes the API sends when a source is degraded.
export const DEGRADED_CLAIMS_REASONS = ['ARCHIVE_UNAVAILABLE', 'CLAIMS_UNAVAILABLE'];

// Match on these too. Workers Comp sends raw error text as its reason, so reason alone misses it.
export const DEGRADED_CLAIMS_SYSTEMS = ['ARCHIVE', 'ENT_CLAIMS', 'EDDS'];

function includesToken(allowlist: string[], value: any): boolean {
  return typeof value === 'string' && allowlist.includes(value.toUpperCase());
}

function isDegradedFailure(failure: any): boolean {
  return (
    includesToken(DEGRADED_CLAIMS_REASONS, _get(failure, 'reason')) ||
    includesToken(DEGRADED_CLAIMS_SYSTEMS, _get(failure, 'system'))
  );
}

/**
 * Checks whether the claims response needs the error banner shown.
 *
 * Fires on a partialFailures entry, the 500's status.failureReason, any 5xx, or a thrown error.
 * Not on 4xx, since a session problem is not a claims outage and the host app handles those.
 * Not on empty data either, because a customer with no claims is healthy.
 *
 * @param claimsResponse {any} - What getClaims returned
 * @returns {boolean} - true when the customer needs to be told
 */
export function hasClaimsRetrievalIssue(claimsResponse: any): boolean {
  if (!claimsResponse) {
    return true;
  }

  // A rejected fetch or bad body comes back as an Error, no status to read.
  if (claimsResponse instanceof Error) {
    return true;
  }

  // Total failure. The 500 nests this inside status.
  if (_get(claimsResponse, 'status.failureReason', '')) {
    return true;
  }

  // 5xx only. A session 4xx is not a claims outage and the host app handles it.
  if (Number(_get(claimsResponse, 'statusCode', 0)) >= 500) {
    return true;
  }

  // Claims came back, so an entry means a policy is missing or short.
  const partialFailures = _get(claimsResponse, 'partialFailures', []);

  return Array.isArray(partialFailures) && partialFailures.some(isDegradedFailure);
}
